import { useNavigate } from 'react-router-dom';
import { Lock, ShieldCheck, BarChart3, MessageCircle } from 'lucide-react';
import Navbar2 from '@/component/navbar2';
import Footer from '@/components/BrandFooter';
import '../styles/publicPages.css';

function AccountingMock() {
  return (
    <div style={{ width: 300, background: '#fff', borderRadius: 16, border: '1.5px solid #EFE9DA', padding: 18, boxShadow: '0 8px 24px -8px rgba(23,38,58,.12)', fontSize: 13 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <strong style={{ color: '#17263A', fontWeight: 800 }}>Compliance</strong>
        <span style={{ background: '#E6F4EC', color: '#15824E', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 999 }}>FTA Active</span>
      </div>
      {[
        { label: 'VAT return — Q1', sub: 'Submitted to FTA', status: 'Done', color: '#15824E', bg: '#E6F4EC' },
        { label: 'Corporate Tax filing', sub: '9% on profit over AED 375k', status: 'Due soon', color: '#9A6B00', bg: '#FFF8E1' },
        { label: 'Bookkeeping', sub: '187 transactions reconciled', status: 'Synced', color: '#15824E', bg: '#E6F4EC' },
      ].map(({ label, sub, status, color, bg }) => (
        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid #EFE9DA' }}>
          <svg viewBox="0 0 24 24" width={16} height={16} style={{ flexShrink: 0 }}><path d="M5 12l5 5L20 7" stroke={color} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: '#17263A' }}>{label}</div>
            <div style={{ fontSize: 11, color: '#6E7888' }}>{sub}</div>
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, color, background: bg, padding: '2px 8px', borderRadius: 999, flexShrink: 0 }}>{status}</span>
        </div>
      ))}
      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 5 }}>
          <span style={{ color: '#42505F' }}>Investor-ready</span><strong style={{ color: '#17263A' }}>80%</strong>
        </div>
        <div style={{ height: 4, background: '#EFE9DA', borderRadius: 999 }}>
          <div style={{ height: '100%', width: '80%', background: 'linear-gradient(90deg, #FFD54F, #E6B400)', borderRadius: 999 }} />
        </div>
      </div>
    </div>
  );
}

function ConsultingMock() {
  return (
    <div style={{ width: 300, background: '#fff', borderRadius: 16, border: '1.5px solid #E5E8EA', padding: 18, boxShadow: '0 8px 24px -8px rgba(23,38,58,.12)', fontSize: 13 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <strong style={{ color: '#17263A', fontWeight: 800 }}>Readiness Score</strong>
        <span style={{ background: '#E8F3FD', color: '#1976D2', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 999 }}>Live</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 16 }}>
        <div style={{ position: 'relative', width: 80, height: 80, flexShrink: 0 }}>
          <svg viewBox="0 0 120 120" width={80} height={80}>
            <circle cx="60" cy="60" r="52" fill="none" stroke="#E7ECF4" strokeWidth="11"/>
            <circle cx="60" cy="60" r="52" fill="none" stroke="url(#pg)" strokeWidth="11" strokeLinecap="round" strokeDasharray="222 327" transform="rotate(-90 60 60)"/>
            <defs><linearGradient id="pg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#1976d2"/><stop offset="1" stopColor="#5B9BD5"/></linearGradient></defs>
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 18, color: '#17263A' }}>68%</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: '#17263A', marginBottom: 3 }}>Semi-Ready</div>
          <div style={{ fontSize: 11, color: '#6E7888', lineHeight: 1.5 }}>Good preparation; some polishing needed to attract investors.</div>
        </div>
      </div>
      {[
        { label: 'Pitch Deck Review', pct: 85, color: '#1976D2' },
        { label: 'Financial Model', pct: 60, color: '#E6B400' },
        { label: 'Valuation Analysis', pct: 40, color: '#E08A1E' },
      ].map(({ label, pct, color }) => (
        <div key={label} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
            <span style={{ color: '#42505F' }}>{label}</span><strong style={{ color: '#17263A' }}>{pct}%</strong>
          </div>
          <div style={{ height: 3, background: '#E5E8EA', borderRadius: 999 }}>
            <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 999 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

const OPTIONS = [
  {
    key: 'accounting',
    badge: 'Accounting & Tax',
    badgeColor: '#FFD54F',
    badgeText: '#17263A',
    title: 'Bookkeeping, VAT & Corporate Tax',
    desc: 'Full-service accounting and UAE compliance — bookkeeping, VAT registration and quarterly filing, and Corporate Tax handled for you.',
    highlights: [
      'Monthly bookkeeping from AED 1,500/mo',
      'VAT registration & quarterly FTA filing',
      'UAE Corporate Tax registration & filing',
      'Investor-ready financial reporting',
    ],
    imgBg: '#FFF8E1',
    MockVisual: AccountingMock,
    cta: 'See accounting plans →',
    path: '/accounting/pricing',
    popular: true,
    accentBorder: '#E6B400',
    btnBg: '#FFD54F',
    btnColor: '#17263A',
  },
  {
    key: 'consulting',
    badge: 'Capital Readiness',
    badgeColor: '#1976D2',
    badgeText: '#fff',
    title: 'Fundraising & Investor Readiness',
    desc: 'From your investor readiness score and financial model to pitch deck creation and full capital raise execution — we take you all the way.',
    highlights: [
      'Free Investor Readiness Score',
      'Pitch deck, financial model & valuation',
      'Capital raise execution & investor pipeline',
      'One-on-one advisor sessions',
    ],
    imgBg: '#E8F3FD',
    MockVisual: ConsultingMock,
    cta: 'See consulting plans →',
    path: '/capital-raising/pricing',
    popular: false,
    accentBorder: '#1976D2',
    btnBg: '#17263A',
    btnColor: '#FFFDF7',
  },
];

export default function Pricing() {
  const navigate = useNavigate();

  return (
    <div className="pub-page">
      <div style={{ position: 'sticky', top: 0, zIndex: 50 }}>
        <Navbar2 bgColor="white" logoTo="/" />
      </div>

      {/* ── HERO ── */}
      <section className="pub-hero cream" style={{ textAlign: 'center', paddingBottom: 80 }}>
        <div className="pub-inner narrow">
          <span className="pub-eye" style={{ marginBottom: 22, display: 'inline-flex' }}>Pricing</span>
          <h1 className="pub-h1" style={{ color: '#17263A', marginBottom: 20 }}>
            Simple, transparent <span className="pub-hl">pricing</span>
          </h1>
          <p className="pub-lead" style={{ margin: '0 auto', textAlign: 'center', maxWidth: 560 }}>
            Choose the service that fits your business. Both are built for UAE startups and SMEs — no hidden fees, no lock-ins.
          </p>
        </div>
      </section>

      {/* ── SELECTOR ── */}
      <section className="pub-sec white" style={{ paddingTop: 0, marginTop: -52 }}>
        <div className="pub-inner">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
            {OPTIONS.map(({ key, badge, badgeColor, badgeText, title, desc, highlights, imgBg, MockVisual, cta, path, popular, accentBorder, btnBg, btnColor }) => (
              <div
                key={key}
                style={{
                  background: '#fff',
                  border: `2px solid ${popular ? accentBorder : '#E8E2D2'}`,
                  borderRadius: 28,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'transform .22s cubic-bezier(.2,.7,.3,1), box-shadow .22s, border-color .18s',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onMouseEnter={e => {
                  const el = e.currentTarget;
                  el.style.transform = 'translateY(-6px)';
                  el.style.boxShadow = '0 32px 72px -24px rgba(23,38,58,.22)';
                  el.style.borderColor = accentBorder;
                }}
                onMouseLeave={e => {
                  const el = e.currentTarget;
                  el.style.transform = 'translateY(0)';
                  el.style.boxShadow = 'none';
                  el.style.borderColor = popular ? accentBorder : '#E8E2D2';
                }}
                onClick={() => navigate(path)}
              >
                {popular && (
                  <div style={{
                    position: 'absolute', top: 20, right: 20,
                    background: '#17263A', color: '#FFD54F',
                    fontSize: 10, fontWeight: 700, letterSpacing: '.12em',
                    textTransform: 'uppercase', padding: '4px 12px', borderRadius: 999,
                    zIndex: 1,
                  }}>
                    Popular
                  </div>
                )}

                {/* Visual preview */}
                <div style={{ background: imgBg, padding: '40px 0 0', display: 'flex', justifyContent: 'center', alignItems: 'flex-end', minHeight: 220 }}>
                  <MockVisual />
                </div>

                {/* Content */}
                <div style={{ padding: '32px 36px 36px', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center',
                    background: badgeColor, color: badgeText,
                    fontSize: 11, fontWeight: 700, letterSpacing: '.14em',
                    textTransform: 'uppercase', padding: '5px 13px', borderRadius: 999,
                    alignSelf: 'flex-start',
                  }}>
                    {badge}
                  </span>

                  <div>
                    <h2 style={{ margin: '0 0 8px', fontWeight: 800, fontSize: 22, color: '#17263A', letterSpacing: '-.02em', lineHeight: 1.2 }}>
                      {title}
                    </h2>
                    <p style={{ margin: 0, fontSize: 14.5, color: '#42505F', lineHeight: 1.72 }}>{desc}</p>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 9, margin: '4px 0' }}>
                    {highlights.map(h => (
                      <div key={h} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <svg viewBox="0 0 24 24" width={16} height={16} style={{ flexShrink: 0, marginTop: 3 }}>
                          <path d="M5 12l5 5L20 7" stroke="#15824E" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span style={{ fontSize: 13.5, color: '#42505F', lineHeight: 1.6 }}>{h}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={e => { e.stopPropagation(); navigate(path); }}
                    style={{
                      marginTop: 'auto',
                      height: 48,
                      borderRadius: 999,
                      border: 'none',
                      background: btnBg,
                      color: btnColor,
                      fontFamily: 'Montserrat, system-ui, sans-serif',
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: 'pointer',
                    }}
                  >
                    {cta}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ── */}
      <section className="pub-sec tinted" style={{ paddingTop: 52, paddingBottom: 52 }}>
        <div className="pub-inner">
          <div style={{ display: 'flex', gap: 48, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
            {[
              { Icon: Lock, text: 'No lock-in contracts' },
              { Icon: ShieldCheck, text: 'UAE-regulated & compliant' },
              { Icon: BarChart3, text: 'Transparent pricing' },
              { Icon: MessageCircle, text: 'Dedicated account manager' },
            ].map(({ Icon, text }) => (
              <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Icon className="w-5 h-5" style={{ color: '#42505F' }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: '#42505F' }}>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NOT SURE? ── */}
      <section className="pub-sec white" style={{ paddingTop: 64, paddingBottom: 80 }}>
        <div className="pub-inner narrow" style={{ textAlign: 'center' }}>
          <h2 className="pub-h2" style={{ color: '#17263A', marginBottom: 16 }}>Not sure which to choose?</h2>
          <p className="pub-lead" style={{ margin: '0 auto 32px', textAlign: 'center' }}>
            Book a free 20-minute call and we'll recommend the right service for where your business is today.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="mailto:support@conncct.com" className="pub-btn primary">
              Book a free call →
            </a>
            <a href="/contact" className="pub-btn ghost-light" style={{ borderColor: '#C0B090', color: '#42505F' }}>
              Contact us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
